import type { TenantStateSet } from '@chatbot/shared-types';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { stateLabel } from '../../lib/state-labels.ts';
import type { TicketRow as Ticket } from '../../lib/types.ts';

export interface TicketDetailPanelProps {
  ticket: Ticket;
  states: TenantStateSet;
  tenantId: string;
  onAssign: (ticket: Ticket, assignedTo: string) => Promise<void>;
  onReopen: (ticket: Ticket, status: string) => Promise<void>;
}

/**
 * El detalle del ticket, con la conversación a un clic.
 *
 * Ese enlace es la razón de existir de este producto y no de un helpdesk: quien
 * toma el ticket ve lo que la persona ya contó, sin pedirle que lo repita. No
 * es un adorno del detalle — es el detalle.
 */
export function TicketDetailPanel({
  ticket,
  states,
  tenantId,
  onAssign,
  onReopen,
}: TicketDetailPanelProps) {
  const [destino, setDestino] = useState('');
  const [trabajando, setTrabajando] = useState(false);

  const cerrado = ticket.stateTerminal === true;
  // Al primer estado abierto que el tenant declaró. No a un nombre fijo.
  const primerAbierto = states.find((estado) => !estado.terminal);

  async function conBloqueo(fn: () => Promise<void>) {
    setTrabajando(true);
    try {
      await fn();
    } finally {
      setTrabajando(false);
    }
  }

  return (
    <aside className="flex h-full flex-col gap-4 overflow-auto border-edge-subtle border-l p-6">
      <div className="flex flex-col gap-1">
        <h2 className="text-h4 text-content-primary">{ticket.summary ?? 'Sin asunto'}</h2>
        <span className="text-caption text-content-tertiary">
          {stateLabel(states, ticket.status)}
          {ticket.assignedTo ? ` · ${ticket.assignedTo}` : ' · Sin asignar'}
        </span>
      </div>

      {ticket.detail ? (
        <p className="whitespace-pre-wrap text-body text-content-secondary">{ticket.detail}</p>
      ) : null}

      <Link
        to={`/conversations/${tenantId}/${ticket.userId}`}
        className="text-body text-content-brand underline underline-offset-2"
      >
        Ver la conversación
      </Link>

      <div className="flex flex-col gap-2 border-edge-subtle border-t pt-4">
        <label htmlFor="asignar-a" className="text-caption text-content-tertiary">
          Asignar a
        </label>
        <div className="flex gap-2">
          <input
            id="asignar-a"
            value={destino}
            onChange={(e) => setDestino(e.target.value)}
            placeholder="Identificador de la persona"
            className="flex-1 rounded-md border border-edge bg-surface-raised px-3 py-2 text-body text-content-primary"
          />
          <button
            type="button"
            disabled={!destino.trim() || trabajando}
            onClick={() => conBloqueo(() => onAssign(ticket, destino.trim()))}
            // Mismo caso que el chip de filtro: `bg-surface-brand` no existe y
            // el botón «Asignar» quedaba en blanco sobre blanco.
            className="rounded-md bg-primary-600 px-4 py-2 text-body text-white disabled:opacity-50"
          >
            Asignar
          </button>
        </div>
      </div>

      {/*
       * Reabrir sólo si está cerrado, y sólo si el tenant tiene a dónde volver.
       * Ofrecerlo sobre uno abierto no significa nada, y un tenant que declaró
       * únicamente estados cerrados no tiene destino posible.
       */}
      {cerrado && primerAbierto ? (
        <button
          type="button"
          disabled={trabajando}
          onClick={() => conBloqueo(() => onReopen(ticket, primerAbierto.id))}
          className="self-start rounded-md border border-edge px-4 py-2 text-body text-content-primary disabled:opacity-50"
        >
          Reabrir como «{primerAbierto.label}»
        </button>
      ) : null}
    </aside>
  );
}
